import { ChangeDatasourceDescriptionAction } from '../../content/services/ChangeDatasourceDescriptionAction';
import { ContentActions, ContentActionsService } from '../../commonInfra/contentActions/ContentActions';
import { DatasourceSpaceOwnProps } from '../../app/spaces/DatasourceSpace/DatasourceSpace';
import { DeleteContentsAction } from '../../content/services/DeleteContentsAction';
import { DetailsDialogInAngularPlaceHelper, IDetailsDialogInAngularPlaceHelper } from '../../app/spaces/DetailsDialog/AngularContentDetailsDialog';
import { FeatureFlags } from '../FeatureFlags';
import { getAppStore } from '../../app/AppStore';
import {
  IAllowedActionScope,
  IChangeDescriptionActionScope,
  IChangeOwnerActionScope,
  IChangeTagsActionScope,
  IDeleteActionScope,
  IGetUserUrlScope,
  RefreshContentScope
  } from '../../app/ActionPlaceScopes';
import { IDatasourceActions } from '../ServerApi/types/IDatasourceActions';
import { IDataSourceDetailInfo, IDataSourceInfo } from '../models/IDataSourceInfo';
import { IDatasourceUsageInfo } from '../ServerApi/types/IUsageInfo';
import { IItemStatisticsInfo } from '../models/resourceInfoParts';
import { IPlaceScope } from '../../app/IPlaceScope';
import { IUserInfo } from '../models/IUserInfo';
import { Location } from '../../common/react/Location';
import { ModalService } from '../services/ModalService';
import { Module } from '../Module';
import { RemoteQueryAgentHelper } from '../helpers/RemoteQueryAgentHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import {
  RevisionHistoryActionInstance
} from '../../revisionHistory/services/RevisionHistoryAction';
import { Set } from '../models/Set';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { ToasterService } from '../services/ToasterService';

// Scope and props needed to display the details dialog
export interface IDatasourceDetailsDialogScope extends
  IAllowedActionScope<IDatasourceActions>,
  IChangeDescriptionActionScope,
  IChangeOwnerActionScope,
  IChangeTagsActionScope,
  IDeleteActionScope,
  IPlaceScope,
  RefreshContentScope,
  IGetUserUrlScope
{
  datasource: IDataSourceDetailInfo & IItemStatisticsInfo<IDatasourceUsageInfo>;
  datasourceSet: Set<IDataSourceDetailInfo>;
  showRemoteQueryAgent: boolean;
  moveToProject: ($event: JQueryEventObject) => void;
  showRevisionHistory: ($event: JQueryEventObject) => any;
  changeRemoteQueryAgent: ($event: JQueryEventObject) => any;
  showDatasourceCertification: boolean;
  certificationStatusUpdated: ($event: JQueryEventObject) => void;
  currentRemoteQueryAgentDisplayName: () => string;
  datasourceSpaceProps: DatasourceSpaceOwnProps;

  onNlpSettingSaved: () => void;
  getProjectUrl: (projectId: string) => string;
}

export class DatasourceDetailsDialogCtrl {
  private ContentActions: ContentActions;
  public static $inject = [
    '$q',
    '$scope',
    'datasourceActions',
    'datasourceDetailInfo',
    'datasourceInfo',
    'ModalService',
    'detailsDialogHelper',
  ];

  constructor(
            $q: ng.IQService,
    private $scope: IDatasourceDetailsDialogScope,
            datasourceActions: IDatasourceActions,
            datasourceDetailInfo: IDataSourceDetailInfo,
    private datasourceInfo: IDataSourceInfo,
            ModalService: ModalService,
            detailsDialogHelper: DetailsDialogInAngularPlaceHelper
  ) {
    this.ContentActions = ContentActionsService;
    const allowedActionFn = (actionName: keyof IDatasourceActions) => (!!datasourceActions && datasourceActions[actionName]);
    const refreshContentFn = () => window.location.reload();

    this.loadDatasourceIntoScope(datasourceDetailInfo);
    this.initScopeForDetailsPane($scope, $q, allowedActionFn, refreshContentFn, ModalService, detailsDialogHelper, ToasterService.instance);
  }

  private loadDatasourceIntoScope(newDatasourceDetailInfo: IDataSourceDetailInfo) {
    const datasourceWithUsageInfo = {
      usageInfo: this.datasourceInfo.usageInfo,
      usageInfoMaxValues: this.datasourceInfo.usageInfoMaxValues,
      ...newDatasourceDetailInfo
    };

    this.$scope.datasource = datasourceWithUsageInfo;
    this.$scope.datasourceSet = Set.singletonForIdItem(newDatasourceDetailInfo);

    this.$scope.objectName = newDatasourceDetailInfo.name;
    this.$scope.ownerId = newDatasourceDetailInfo.owner.id;
  }

  private initScopeForDetailsPane(
      $scope: IDatasourceDetailsDialogScope,
      $q: ng.IQService,
      allowedActionFn: (actionName: string) => boolean,
      refreshContentFn: () => void,
      modalService: ModalService,
      dialogHelper: IDetailsDialogInAngularPlaceHelper,
      toaster: ToasterService
  ) {

    // $scope.refreshContent is used by some of the menu actions too; be very careful about deleting it
    $scope.refreshContent = refreshContentFn;
    $scope.allowedAction = allowedActionFn;

    $scope.del = ($event: JQueryEventObject) => {
      DeleteContentsAction.confirmDeleteContents([$scope.datasource])
      .then((result) => {
        toaster.markToastsAsDoNotDismissOnLocationChange();
        const siteUrls = siteUrlsSelector(getAppStore().getState());
        Location.instance.setUrl(siteUrls.projectUrl($scope.datasource.project.id));
      });
    };

    $scope.changeDescription = (newDescription: string) => {
      return ChangeDatasourceDescriptionAction.execute($scope.datasource, newDescription)
        .then(dialogHelper.inlineEditResultHandler);
    };

    $scope.changeOwner = ($event: JQueryEventObject) => {
      return this.ContentActions.changeOwnerOnDatasources($scope.datasourceSet)
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    $scope.showRevisionHistory = () => {
      return RevisionHistoryActionInstance.showDataSourceRevisionHistory($scope.datasource)
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    $scope.moveToProject = () => {
      return this.ContentActions.moveDatasourcesToProject($scope.datasourceSet)
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    // Allow direct assignment of a remote query agent to a datasource
    $scope.showRemoteQueryAgent = FeatureFlags.bridgeLiveDirectAssignEnabled &&
      !$scope.datasource.hasExtracts &&
      !$scope.datasource.testingConnectionAllowed;

    $scope.currentRemoteQueryAgentDisplayName = () => {
      return RemoteQueryAgentHelper.getRemoteQueryAgentDisplayName($scope.datasource);
    };

    $scope.changeRemoteQueryAgent = () => {
      return this.ContentActions.changeRemoteQueryAgentOnDatasource($scope.datasource)
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    $scope.showDatasourceCertification = FeatureFlags.datasourceCertificationEnabled;

    $scope.certificationStatusUpdated = () => {
      const fakeResult: null = null; // The handler needs a result, but it ignores it if not present
      dialogHelper.inlineEditResultHandler(fakeResult);
    };

    $scope.changeTags = (newTagsList: string[]) => {
      return this.ContentActions.setTagsOnDatasource($scope.datasource, newTagsList)
        .then(ResultHelper.rejectResultOnError($q))
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    $scope.onNlpSettingSaved = () => {
      dialogHelper.actionFromSeparateDialogResultHandler();
      dismissDialog();
    };

    $scope.getProjectUrl = (projectId: string): string => {
      const siteUrls = siteUrlsSelector(getAppStore().getState());
      return siteUrls.projectUrl(projectId);
    };

    $scope.getUserUrl = (user: IUserInfo): string => {
      const siteUrls = siteUrlsSelector(getAppStore().getState());
      return siteUrls.userUrl(user.username, user.domainName);
    };

    function dismissDialog() {
      modalService.dismissAll();
    }
  }
}

Module.instance().controller('DatasourceDetailsDialogCtrl', DatasourceDetailsDialogCtrl);
